import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';
import {
  PageTemplateDashboard,
  PageTemplateHeader,
  PageTemplateHeaderActions
} from '@sierralabs/react-material-ui';

import { FetchError } from '../../common/api/api';
import HoveredCard from '../../common/card/HoveredCard';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import HazardActionInfoCard from './cards/HazardActionInfoCard';
import {
  useFindHazardActionById,
  useFindHazardActionVersion,
  useHazardEntityPrefixId
} from './Hazard.hooks';
import {
  BaseHazardActionsCardProps,
  HazardAction,
  HazardBoardColumnEntity
} from './Hazard.interface';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import HazardReferenceCard from './cards/HazardReferenceCard';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { useJiraServerContext } from '../jira-server/jira-server.context';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';

import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';

const HazardActionDetailPage: React.FC = () => {
  const {
    hazardId: paramHazardId = '',
    productId: paramProductId = '',
    actionId: paramCauseId = ''
  } = useParams<{ hazardId: string; productId: string; actionId: string }>();
  const hazardId: number = parseInt(paramHazardId) || 0;
  const productId: number = parseInt(paramProductId) || 0;
  const actionId: number = parseInt(paramCauseId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [hazardAction, setHazardAction] = useState<HazardAction | undefined>();
  const [, setDidUpdate] = useState<boolean>(false);
  const [, setError] = useState<FetchError | undefined>();
  const {
    hazardAction: data,
    isLoadingHazardAction,
    findHazardActionError,
    findHazardAction
  } = useFindHazardActionById(hazardId, actionId);
  const {
    hazardActionVersion: loadedHazardActionVersion,

    findHazardActionVersion
  } = useFindHazardActionVersion(hazardId, actionId, version);
  const [hazardActionVersion, setHazardActionVersion] = useState(
    loadedHazardActionVersion
  );

  const title: string = useHazardEntityPrefixId(
    hazardAction,
    HazardBoardColumnEntity.Action
  );
  const cardProps: BaseHazardActionsCardProps = {
    hazardId,
    hazardAction: hazardAction!,
    hazardActionVersion,
    onChange: (newHazardCause: Partial<HazardAction>) => {
      if (hazardAction) {
        // notify task components to refresh
        hazardAction.task = { ...hazardAction.task };
        setHazardAction({
          ...hazardAction,
          ...newHazardCause
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!hazardAction?.task) {
      return;
    }
    Object.assign(hazardAction.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findHazardAction();
  }, [findHazardAction, hazardId, actionId]);
  useEffect(() => {
    if (version) {
      findHazardActionVersion();
    } else {
      setHazardActionVersion(undefined);
    }
  }, [findHazardActionVersion, hazardId, actionId, version]);
  useEffect(
    () =>
      loadedHazardActionVersion &&
      setHazardActionVersion(loadedHazardActionVersion),
    [loadedHazardActionVersion]
  );

  useEffect(() => {
    data && setHazardAction(data);
  }, [data]);

  useEffect(() => {
    setError(findHazardActionError);
  }, [findHazardActionError]);
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useJiraServerContext();
  const { isFieldDisabled } = useTaskEntityFields(hazardAction?.task);
  const task = hazardActionVersion?.task || hazardAction?.task;

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton
          to={
            from !== 'hazardTrace'
              ? `/portal/products/${productId}/hazards/${hazardId}/${from}`
              : `/portal/products/${productId}/${from}`
          }
        />
        <HeaderTitle>
          <h2>{isLoadingHazardAction ? 'Loading...' : title}</h2>
          {hazardAction && (
            <div className='status-container'>
              <TaskStatusLabel task={hazardAction.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          {hazardAction?.task && (
            <TaskStatusActions
              task={hazardAction.task}
              taskEntity={hazardAction}
              onChange={(task: Task) => {
                hazardAction.task = task;
                setHazardAction({ ...hazardAction });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {hazardAction && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {hazardActionVersion &&
            ` from ${formatDate(hazardActionVersion.created)}. `}
          <Link to={`./${hazardAction.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {hazardAction && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <HazardActionInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={hazardAction.task}
                      entity={{
                        title: hazardAction.title,
                        description: hazardAction.description
                      }}
                      jiraFieldColumns={
                        environment.hazardConfig?.action?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update hazardAction Task jira issues on change
                        hazardAction.task.jiraIssues = taskJiraIssues;
                        setHazardAction({ ...hazardAction });
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    task={hazardAction.task}
                    disabled={Boolean(hazardActionVersion)}
                    attachments={hazardActionVersion?.attachments}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TaskStatusVersionCard
                    taskEntity={hazardAction}
                    renderContent={group =>
                      group.version !== hazardAction.task.version && (
                        <HoveredCard
                          title={`Version ${group.version}`}
                          content='View the previous version'
                          actions={
                            <Button
                              variant='text'
                              color='primary'
                              component={Link}
                              startIcon={<Visibility />}
                              to={`./${hazardAction.id}?ver=${group.version}`}
                            >
                              View
                            </Button>
                          }
                        />
                      )
                    }
                  />
                </Grid>
                {task?.children && task.children.length > 0 && (
                  <Grid item xs={12}>
                    <SubTaskListCard
                      title='Stakeholders'
                      task={task}
                      disabled={
                        Boolean(version) || isFieldDisabled('assignSubTasks')
                      }
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
                {task && (
                  <Grid item xs={12}>
                    <TaskAssignCard task={task} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TaskFileListCard
                    task={hazardAction.task}
                    disabled={Boolean(hazardActionVersion)}
                    onError={cardProps.onError}
                    onChange={handleTaskUpdate}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HazardReferenceCard
                    hazardId={hazardId}
                    productId={productId}
                    sourceEntityId={actionId}
                    version={version}
                    source='actions'
                    target='controls'
                    disabled={isFieldDisabled('assignControls')}
                  />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title='Hazard Action'
                      apiPath={`hazards/${hazardId}/actions`}
                      entity={hazardAction}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>
    </StyledPageTemplate>
  );
};

export default HazardActionDetailPage;
